import { createI18n } from 'vue-i18n'
import type { I18n } from 'vue-i18n'
import type { WritableComputedRef } from 'vue'
import axios from 'axios'
import { getParam } from '@/utils/web-location'
import { useLoading } from '@/components/common/loading-overlay/useLoading'

export const supportedLangs = [
  'th',
  'en',
  'my',
  'kh',
  'lo',
  'cn',
  'ru',
] as const
export type LangKey = (typeof supportedLangs)[number]

let fallbackTh: Record<string, any> | null = null
async function loadFallbackTh(): Promise<Record<string, any>> {
  if (fallbackTh) return fallbackTh
  try {
    fallbackTh = (await import('../locales/th.json')).default
  } catch (err) {
    console.error('[i18n] th.json fallback failed:', err)
    fallbackTh = { title: 'สมัครสมาชิกมายโลตัส' }
  }
  return fallbackTh
}

async function fetchLocaleMessages(
  lang: LangKey,
): Promise<Record<string, any>> {
  try {
    const { data } = await axios.get(`/api/v1/locales/${lang}`, {
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })
    return data.data
  } catch (err) {
    console.warn(`[i18n] Failed to load "${lang}". Falling back to th.json`)
    console.warn('error detail:', err)
    return await loadFallbackTh()
  }
}

const loadedLanguages = new Set<LangKey>()
let i18n: I18n<Record<string, any>, Record<string, any>>

export async function createI18nInstance(): Promise<I18n> {
  try {
    let lang = getParam<LangKey>('lang')
    lang = supportedLangs.includes(lang!) ? lang : 'th'

    const [currentLangMessages, englishMessages] = await Promise.all([
      fetchLocaleMessages(lang!),
      lang == 'en' ? null : fetchLocaleMessages('en'),
    ])

    i18n = createI18n({
      legacy: false,
      locale: lang!,
      fallbackLocale: 'en',
      messages: {
        [lang!]: currentLangMessages,
        ...(englishMessages && { en: englishMessages }),
      },
    })

    loadedLanguages.add(lang!)
    if (englishMessages) {
      loadedLanguages.add('en')
    }

    return i18n
  } catch (fatalError) {
    console.error(
      '[i18n] FATAL: Failed to initialize i18n. Using hard fallback.',
      fatalError,
    )

    return createI18n({
      legacy: false,
      locale: 'th',
      fallbackLocale: 'en',
      messages: {
        th: { title: 'สมัครสมาชิกมายโลตัส' },
      },
    })
  }
}

export async function setLanguage(lang: LangKey): Promise<void> {
  const { showLoading, hideLoading } = useLoading()

  if (!i18n) {
    console.warn('[i18n] i18n instance is not ready yet.')
    return
  }
  showLoading()

  try {
    if (!loadedLanguages.has(lang)) {
      const messages = await fetchLocaleMessages(lang)
      i18n.global.setLocaleMessage(lang, messages)
      loadedLanguages.add(lang)
    }

    ;(i18n.global.locale as WritableComputedRef<string>).value = lang
  } catch (err) {
    console.error(`[i18n] Failed to change language to "${lang}"`, err)
  } finally {
    hideLoading()
  }
}

export function getI18n(): I18n {
  return i18n
}
